import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ExternalLinkIcon, UsersIcon, XIcon, ClipboardListIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NonprofitScroller } from './NonprofitScroller';
import { CATEGORIES, NonprofitCategory } from '../data/nonprofit-categories';
/* Data moved to data/nonprofit-categories.ts
const _CATEGORIES_REMOVED = [
  {
    id: 'immigration',
    label: 'Immigration & Legal Aid',
    labelEs: 'Inmigración y Asistencia Legal',
    icon: ScaleIcon,
    gradient: 'from-haa-blue to-haa-navy',
    borderColor: 'border-haa-blue',
    accentColor: 'text-haa-blue',
    bgColor: 'bg-blue-50',
    nonprofits: [
      {
        name: 'National TPS Alliance',
        description:
          'A grassroots organization led by TPS holders advocating for permanent residency and protection for Temporary Protected Status beneficiaries, including Hondurans.',
        descriptionEs:
          'Una organización de base liderada por beneficiarios de TPS que aboga por la residencia permanente y la protección de los beneficiarios del Estatus de Protección Temporal, incluyendo hondureños.',
        url: 'https://www.nationaltpsalliance.org/',
      },
      {
        name: 'CLINIC — Catholic Legal Immigration Network',
        description:
          'A network of nonprofit immigration legal services programs that provides affordable, high-quality immigration legal services to low-income immigrants.',
        descriptionEs:
          'Una red de programas de servicios legales de inmigración sin fines de lucro que brinda servicios legales de inmigración asequibles y de alta calidad a inmigrantes de bajos ingresos.',
        url: 'https://cliniclegal.org/',
      },
      {
        name: 'CASA',
        description:
          'Builds power with immigrant and working-class communities in the Mid-Atlantic and beyond to create a more just society.',
        descriptionEs:
          'Construye poder con comunidades inmigrantes y de clase trabajadora en el Atlántico Medio y más allá para crear una sociedad más justa.',
        url: 'https://wearecasa.org/',
        location: 'MD, VA, PA, GA',
      },
      {
        name: 'National Immigration Law Center (NILC)',
        description:
          'Defends and advances the rights of immigrants with low income through litigation, policy advocacy, and capacity building.',
        descriptionEs:
          'Defiende y promueve los derechos de inmigrantes de bajos ingresos a través de litigios, defensa de políticas y desarrollo de capacidades.',
        url: 'https://www.nilc.org/',
      },
      {
        name: 'Immigration Advocates Network',
        description:
          'Provides a free online directory of legal service providers and immigration resources across the United States to help immigrants find trusted legal help.',
        descriptionEs:
          'Proporciona un directorio gratuito en línea de proveedores de servicios legales y recursos de inmigración en todo Estados Unidos para ayudar a los inmigrantes a encontrar ayuda legal confiable.',
        url: 'https://www.immigrationadvocates.org/',
      },
      {
        name: 'RAICES',
        description:
          'Provides free and low-cost legal services to Honduran and other Central American immigrants and refugees in the United States.',
        descriptionEs:
          'Brinda servicios legales gratuitos y de bajo costo a inmigrantes y refugiados hondureños y de otros países centroamericanos en Estados Unidos.',
        url: 'https://www.raicestexas.org/',
        location: 'TX and nationwide',
      },
      {
        name: 'American Immigration Council',
        description:
          'Works to strengthen America by shaping how America thinks about and acts towards immigrants and immigration.',
        descriptionEs:
          'Trabaja para fortalecer a Estados Unidos moldeando cómo el país piensa y actúa respecto a los inmigrantes y la inmigración.',
        url: 'https://www.americanimmigrationcouncil.org/',
      },
      {
        name: 'Ayuda',
        description:
          'Provides direct legal, social, and language access services to low-income immigrants in the Washington, D.C. metro area.',
        descriptionEs:
          'Brinda servicios legales directos, sociales y de acceso lingüístico a inmigrantes de bajos ingresos en el área metropolitana de Washington, D.C.',
        url: 'https://www.ayuda.com/',
        location: 'Washington D.C. metro',
      },
      {
        name: 'UNHCR — Honduras',
        description:
          'The UN Refugee Agency works to protect displaced Hondurans and support durable solutions for refugees and asylum seekers.',
        descriptionEs:
          'La Agencia de la ONU para los Refugiados trabaja para proteger a los hondureños desplazados y apoyar soluciones duraderas para refugiados y solicitantes de asilo.',
        url: 'https://www.unhcr.org/countries/honduras',
        location: 'Tegucigalpa, Honduras',
      },
      {
        name: 'CARECEN — Central American Resource Center',
        description:
          'Provides legal services and advocacy for Central American immigrants, with deep roots serving the Honduran community.',
        descriptionEs:
          'Brinda servicios legales y defensa para inmigrantes centroamericanos, con profundas raíces sirviendo a la comunidad hondureña.',
        url: 'https://www.carecen-la.org/',
        location: 'Los Angeles, CA',
      },
      {
        name: 'U.S. Committee for Refugees and Immigrants (USCRI)',
        description:
          'Protects the rights and addresses the needs of persons in forced or voluntary migration worldwide through advocacy, resettlement, and integration services.',
        descriptionEs:
          'Protege los derechos y atiende las necesidades de personas en migración forzada o voluntaria en todo el mundo a través de defensa, reasentamiento y servicios de integración.',
        url: 'https://refugees.org/',
      },
      {
        name: 'Catholic Charities USA — Immigration Services',
        description:
          'Provides immigration legal services, refugee resettlement, and support for immigrant families through a nationwide network of local Catholic Charities agencies.',
        descriptionEs:
          'Brinda servicios legales de inmigración, reasentamiento de refugiados y apoyo a familias inmigrantes a través de una red nacional de agencias locales de Caridades Católicas.',
        url: 'https://www.catholiccharitiesusa.org/what-we-do/immigration-services/',
      },
      {
        name: 'UnidosUS',
        description:
          'The largest national Hispanic civil rights and advocacy organization in the United States, advancing opportunities for Latino communities through research, policy, and programs.',
        descriptionEs:
          'La organización nacional más grande de derechos civiles y defensa hispana en Estados Unidos, promoviendo oportunidades para las comunidades latinas a través de investigaciones, políticas y programas.',
        url: 'https://unidosus.org/',
      },
      {
        name: 'United We Dream',
        description:
          'The largest immigrant youth-led community in the country, fighting for justice and dignity for immigrants regardless of immigration status.',
        descriptionEs:
          'La comunidad más grande del país liderada por jóvenes inmigrantes, luchando por la justicia y la dignidad de los inmigrantes sin importar su estatus migratorio.',
        url: 'https://unitedwedream.org/',
      },
      {
        name: 'National Immigrant Justice Center (NIJC)',
        description:
          'Provides legal services to immigrants, refugees, and asylum seekers through direct representation, policy reform, and impact litigation.',
        descriptionEs:
          'Brinda servicios legales a inmigrantes, refugiados y solicitantes de asilo a través de representación directa, reforma de políticas y litigios de impacto.',
        url: 'https://immigrantjustice.org/',
      },
      {
        name: 'HIAS',
        description:
          'A global Jewish nonprofit that protects refugees and displaced persons, providing legal services, resettlement support, and advocacy for immigrant rights.',
        descriptionEs:
          'Una organización judía global sin fines de lucro que protege a refugiados y personas desplazadas, brindando servicios legales, apoyo de reasentamiento y defensa de los derechos de los inmigrantes.',
        url: 'https://hias.org/',
      },
      {
        name: 'Kids in Need of Defense (KIND)',
        description:
          'Provides pro bono legal representation to unaccompanied immigrant and refugee children, ensuring they have access to justice and protection.',
        descriptionEs:
          'Brinda representación legal pro bono a niños inmigrantes y refugiados no acompañados, asegurando que tengan acceso a la justicia y protección.',
        url: 'https://supportkind.org/',
      },
    ],
  },
  {
    id: 'education',
    label: 'Education',
    labelEs: 'Educación',
    icon: GraduationCapIcon,
    gradient: 'from-emerald-500 to-emerald-800',
    borderColor: 'border-emerald-500',
    accentColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    nonprofits: [
      {
        name: 'Students Helping Honduras',
        description:
          'Builds schools and supports education access across Honduras through volunteer-driven campaigns and the One Thousand Schools vision.',
        descriptionEs:
          'Construye escuelas y apoya el acceso a la educación en toda Honduras a través de campañas impulsadas por voluntarios y la visión de Mil Escuelas.',
        url: 'https://onethousandschools.com/',
      },
      {
        name: 'Global Brigades — Honduras',
        description:
          'Mobilizes student volunteers to support sustainable development in health, water, and education in Honduran communities.',
        descriptionEs:
          'Moviliza estudiantes voluntarios para apoyar el desarrollo sostenible en salud, agua y educación en comunidades hondureñas.',
        url: 'https://www.globalbrigades.org/',
        location: 'Multiple regions, Honduras',
      },
    ],
  },
  {
    id: 'faith',
    label: 'Faith-Based Organizations',
    labelEs: 'Organizaciones Religiosas',
    icon: CrossIcon,
    gradient: 'from-violet-500 to-violet-800',
    borderColor: 'border-violet-500',
    accentColor: 'text-violet-600',
    bgColor: 'bg-violet-50',
    nonprofits: [
      {
        name: 'Catholic Relief Services — Honduras',
        description:
          'Delivers emergency relief and long-term development programs addressing food security, migration, and resilience in Honduras.',
        descriptionEs:
          'Brinda ayuda de emergencia y programas de desarrollo a largo plazo que abordan la seguridad alimentaria, la migración y la resiliencia en Honduras.',
        url: 'https://www.crs.org/our-work-overseas/where-we-work/honduras',
        location: 'Nationwide, Honduras',
      },
      {
        name: 'World Relief — Honduras',
        description:
          'Partners with local churches to provide humanitarian aid, refugee support, and community development across Honduras.',
        descriptionEs:
          'Se asocia con iglesias locales para brindar ayuda humanitaria, apoyo a refugiados y desarrollo comunitario en toda Honduras.',
        url: 'https://worldrelief.org/',
      },
      {
        name: 'Shoulder to Shoulder',
        description:
          'A faith-inspired organization providing sustainable health care and education to underserved communities in Honduras through U.S. medical partnerships.',
        descriptionEs:
          'Una organización inspirada en la fe que brinda atención médica sostenible y educación a comunidades desatendidas en Honduras a través de alianzas médicas con EE.UU.',
        url: 'https://www.shouldertoshoulder.org/',
        location: 'Intibucá, Honduras',
      },
      {
        name: 'Jesuit Refugee Service — Honduras',
        description:
          'Accompanies, serves, and advocates for refugees and displaced people in Honduras, with a focus on education and legal aid.',
        descriptionEs:
          'Acompaña, sirve y aboga por refugiados y personas desplazadas en Honduras, con enfoque en educación y asistencia legal.',
        url: 'https://www.jrs.net/',
        location: 'Honduras',
      },
    ],
  },
  {
    id: 'leadership',
    label: 'Leadership & Civic Empowerment',
    labelEs: 'Liderazgo y Empoderamiento Cívico',
    icon: LandmarkIcon,
    gradient: 'from-haa-blue to-haa-navy',
    borderColor: 'border-haa-blue',
    accentColor: 'text-haa-blue',
    bgColor: 'bg-blue-50',
    nonprofits: [
      {
        name: 'Honduran-American Association (HAA)',
        description:
          'Elevates the Honduran diaspora in the United States through advocacy, civic engagement, and community empowerment.',
        descriptionEs:
          'Eleva a la diáspora hondureña en Estados Unidos a través de la defensa, la participación cívica y el empoderamiento comunitario.',
        url: 'https://honduranamerican.org/',
        location: 'Washington D.C. metro',
      },
    ],
  },
  {
    id: 'health',
    label: 'Health & Medical Aid',
    labelEs: 'Salud y Asistencia Médica',
    icon: HeartPulseIcon,
    gradient: 'from-rose-500 to-rose-800',
    borderColor: 'border-rose-500',
    accentColor: 'text-rose-600',
    bgColor: 'bg-rose-50',
    nonprofits: [
      {
        name: 'Doctors Without Borders — Honduras',
        description:
          'Provides emergency medical care and mental health support in areas of Honduras affected by violence and displacement.',
        descriptionEs:
          'Brinda atención médica de emergencia y apoyo de salud mental en áreas de Honduras afectadas por la violencia y el desplazamiento.',
        url: 'https://www.msf.org/honduras',
        location: 'San Pedro Sula, Tegucigalpa',
      },
      {
        name: 'Global Brigades — Health',
        description:
          'Sends medical volunteer brigades to provide primary care and preventive health services in rural Honduran communities.',
        descriptionEs:
          'Envía brigadas médicas voluntarias para brindar atención primaria y servicios de salud preventiva en comunidades rurales hondureñas.',
        url: 'https://www.globalbrigades.org/',
        location: 'Multiple regions, Honduras',
      },
      {
        name: 'Clinica Esperanza / Hope Clinic',
        description:
          'Provides free primary health care and preventive services to uninsured patients, including Honduran immigrants in North Carolina.',
        descriptionEs:
          'Brinda atención médica primaria gratuita y servicios preventivos a pacientes sin seguro, incluyendo inmigrantes hondureños en Carolina del Norte.',
        url: 'https://www.clinicaesperanza.org/',
        location: 'Raleigh, NC',
      },
    ],
  },
  {
    id: 'humanitarian',
    label: 'Humanitarian & Disaster Relief',
    labelEs: 'Ayuda Humanitaria y Desastres',
    icon: HomeIcon,
    gradient: 'from-orange-500 to-orange-700',
    borderColor: 'border-orange-500',
    accentColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    nonprofits: [
      {
        name: 'World Central Kitchen — Honduras',
        description:
          'Provides emergency food relief and supports local food systems in Honduras during natural disasters and crises.',
        descriptionEs:
          'Brinda ayuda alimentaria de emergencia y apoya los sistemas alimentarios locales en Honduras durante desastres naturales y crisis.',
        url: 'https://wck.org/',
      },
      {
        name: 'CARE Honduras',
        description:
          'Works to end poverty and social injustice in Honduras through programs in food security, economic empowerment, and disaster response.',
        descriptionEs:
          'Trabaja para acabar con la pobreza y la injusticia social en Honduras a través de programas de seguridad alimentaria, empoderamiento económico y respuesta a desastres.',
        url: 'https://www.care.org/our-work/where-we-work/honduras/',
      },
      {
        name: 'Mercy Corps — Honduras',
        description:
          'Addresses the root causes of poverty and migration in Honduras through economic opportunity and resilience programs.',
        descriptionEs:
          'Aborda las causas fundamentales de la pobreza y la migración en Honduras a través de programas de oportunidad económica y resiliencia.',
        url: 'https://www.mercycorps.org/where-we-work/honduras',
      },
      {
        name: 'UNHCR — Honduras',
        description:
          'The UN Refugee Agency works to protect displaced Hondurans and support durable solutions for refugees and asylum seekers.',
        descriptionEs:
          'La Agencia de la ONU para los Refugiados trabaja para proteger a los hondureños desplazados y apoyar soluciones duraderas para refugiados y solicitantes de asilo.',
        url: 'https://www.unhcr.org/countries/honduras',
        location: 'Tegucigalpa, Honduras',
      },
      {
        name: 'Scalabrinian Sisters — Honduras',
        description:
          'A religious congregation dedicated to serving migrants, refugees, and displaced persons in Honduras and throughout Central America.',
        descriptionEs:
          'Una congregación religiosa dedicada a servir a migrantes, refugiados y personas desplazadas en Honduras y en toda Centroamérica.',
        url: 'https://scalabriniansisters.org/honduras/',
        location: 'Honduras',
      },
      {
        name: 'CLINIC — Catholic Legal Immigration Network',
        description:
          'Offers affordable immigration legal services to Honduran immigrants through a nationwide network of nonprofit providers.',
        descriptionEs:
          'Ofrece servicios legales de inmigración asequibles a inmigrantes hondureños a través de una red nacional de proveedores sin fines de lucro.',
        url: 'https://cliniclegal.org/',
      },
    ],
  },
]
*/
function CategoryAccordion({
  category,
  index
}: {
  category: NonprofitCategory;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const [isOpen, setIsOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const Icon = category.icon;
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.07,
    ease: [0.16, 1, 0.3, 1]
  }} className={`rounded-2xl border-l-4 ${category.borderColor} bg-white shadow-md overflow-hidden`} data-id="element-3119">
      <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-3120">
        <div className="flex items-center gap-3" data-id="element-3121">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-3122">
            <Icon className="w-4.5 h-4.5 text-white w-[18px] h-[18px]" data-id="element-3123" />
          </div>
          <span className={`font-bebas text-xl tracking-wider ${category.accentColor}`} data-id="element-3124">
            {isEs && category.labelEs ? category.labelEs : category.label}
          </span>
          <span className="font-nunito text-xs text-gray-400 font-semibold" data-id="element-3125">
            {category.nonprofits.length}{' '}
            {isEs ? 'organizaciones' : 'organizations'}{' '}
            <span className="italic" data-id="element-3126">
              ({isEs ? 'Más Próximamente' : 'More Coming'})
            </span>
          </span>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex-shrink-0 ml-2" data-id="element-3127">
          <ChevronDownIcon className={`w-5 h-5 ${category.accentColor}`} data-id="element-3128" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-3129">
        {isOpen && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-3130">
            <div className="border-t border-gray-100 px-5 py-4 flex flex-col gap-3" data-id="element-3131">
              {category.nonprofits.map((org, i) => <motion.div key={org.name} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.05
          }} className={`rounded-xl ${category.bgColor} border border-gray-100 px-4 py-3`} data-id="element-3132">
                  <div className="flex items-start justify-between gap-3" data-id="element-3133">
                    <div className="flex-1 min-w-0" data-id="element-3134">
                      <p className={`font-nunito font-bold text-sm ${category.accentColor} leading-snug`} data-id="element-3135">
                        {org.name}
                      </p>
                      {org.location && <p className="font-nunito text-xs text-gray-400 mt-0.5" data-id="element-3136">
                          📍 {org.location}
                        </p>}
                      <p className="font-nunito text-xs text-gray-600 leading-relaxed mt-1" data-id="element-3137">
                        {isEs && org.descriptionEs ? org.descriptionEs : org.description}
                      </p>
                    </div>
                    {org.url && <a href={org.url} target="_blank" rel="noopener noreferrer" aria-label={`${isEs ? 'Visitar' : 'Visit'} ${org.name}`} className={`flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br ${category.gradient} flex items-center justify-center shadow-sm hover:scale-110 transition-transform`} data-id="element-3138">
                        <ExternalLinkIcon className="w-3.5 h-3.5 text-white" data-id="element-3139" />
                      </a>}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function NonprofitDirectory() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  // Form State
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactName, setContactName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [is501c3, setIs501c3] = useState<'yes' | 'no' | 'not-sure' | null>(null);
  const [ein, setEin] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [socialMedia, setSocialMedia] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const resetForm = () => {
    setContactName('');
    setOrgName('');
    setIs501c3(null);
    setEin('');
    setPhone('');
    setEmail('');
    setWebsite('');
    setSocialMedia('');
    setCategory('');
    setLocation('');
    setDescription('');
    setSubmitted(false);
  };
  const NONPROFIT_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbyUUpnMnKlAgNxNBXGn424XnGDhoFD1703PL7C8zujLV10PWc07wpAlCjpI6d8bXGCLaw/exec';
  const handleSubmit = () => {
    const data = {
      contactName,
      orgName,
      is501c3,
      ein: is501c3 === 'yes' ? ein : '',
      phone,
      email,
      website,
      socialMedia,
      category,
      location,
      description,
      timestamp: new Date().toISOString()
    };
    fetch(NONPROFIT_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Nonprofit Google Sheets submission error:', err);
    });
    setSubmitted(true);
  };
  const isValid = contactName.trim() && orgName.trim() && email.trim() && description.trim();
  return <section id="nonprofit-directory" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="nonprofit-directory-heading" data-id="element-3140">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3141">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-14" data-id="element-3142">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-3143">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3144">
              {isEs ? 'Recursos y Apoyo' : 'Resources & Support'}
            </span>
            <h2 id="nonprofit-directory-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3145">
              {isEs ? 'Directorio de Organizaciones' : 'Nonprofit Directory'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-3146">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3147" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3148" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3149" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-3150">
              {isEs ? 'Un directorio curado de organizaciones confiables que sirven a la comunidad hondureño-americana.' : 'A curated directory of trusted organizations serving the Honduran-American community.'}
            </p>
            <div className="mt-5 max-w-2xl mx-auto bg-haa-skyblue/10 border border-haa-skyblue/30 rounded-xl px-5 py-4 text-sm font-nunito text-gray-700 leading-relaxed" data-id="element-3151">
              {isEs ? 'Trabajamos continuamente para verificar organizaciones sin fines de lucro registradas como 501(c)(3) que sirven a Honduras y la diáspora hondureño-americana. La inclusión en este directorio no constituye un respaldo. Por favor, haga su propia investigación antes de donar o ser voluntario.\n\nSi tiene preguntas, comuníquese con nosotros \u2014 podemos ayudarle a encontrar y conectarse con organizaciones de confianza.' : 'We continuously work to vet registered 501(c)(3) nonprofits serving Honduras and the Honduran-American diaspora. Inclusion in this directory does not constitute an endorsement. Please do your due diligence before donating or volunteering. \n\nIf you have questions, reach out to us \u2014 we can help you find and connect with reputable organizations.'}
            </div>
          </motion.div>
        </div>

        {/* Featured Nonprofit Scroller */}
        <div className="mb-14" data-id="element-3152">
          <NonprofitScroller data-id="element-3153" />
        </div>

        {/* Category Accordions */}
        <div className="max-w-3xl mx-auto flex flex-col gap-4" data-id="element-3154">
          {CATEGORIES.map((category, i) => <CategoryAccordion key={category.id} category={category} index={i} data-id="element-3155" />)}
        </div>

        {/* Footer note */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.7
      }} className="mt-12 max-w-3xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-haa-blue/5 border border-haa-blue/20 rounded-2xl p-6" data-id="element-3156">
          <div className="flex items-start gap-4" data-id="element-3157">
            <UsersIcon className="w-6 h-6 text-haa-blue flex-shrink-0 mt-0.5" data-id="element-3158" />
            <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-3159">
              <span className="font-bold text-haa-navy block mb-1" data-id="element-3160">
                {isEs ? '¿Conoce una organización que deberíamos agregar?' : 'Know an organization we should add?'}
              </span>
              {isEs ? 'Si usted representa o conoce una organización sin fines de lucro que sirve a la comunidad hondureño-americana, nos encantaría incluirla en este directorio.' : "If you represent or know of a nonprofit serving the Honduran-American community, we'd love to include them in this directory."}
            </p>
          </div>
          <button onClick={() => setModalOpen(true)} className="flex-shrink-0 w-full sm:w-auto py-2.5 px-5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors whitespace-nowrap" data-id="element-3161">
            {isEs ? 'Sugerir Organización' : 'Submit Organization'}
          </button>
        </motion.div>
      </div>
    </section> /* Form Modal */;
  {
    /* Form Modal */
  }
  ;
  <AnimatePresence data-id="element-3162">
    {modalOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => {
      setModalOpen(false);
      if (submitted) resetForm();
    }} data-id="element-3163">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3164">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-3165">
            <div className="flex items-center gap-3" data-id="element-3166">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-3167">
                <ClipboardListIcon className="w-5 h-5 text-white" data-id="element-3168" />
              </div>
              <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider leading-none pt-1" data-id="element-3169">
                {isEs ? 'Organizaciones que Apoyan a Honduras y Hondureño-Americanos' : 'Nonprofits Supporting Honduras & Honduran-Americans'}
              </h2>
            </div>
            <button onClick={() => {
            setModalOpen(false);
            if (submitted) resetForm();
          }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label="Close" data-id="element-3170">
              <XIcon className="w-5 h-5" data-id="element-3171" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-3172">
            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center justify-center py-10 text-center" data-id="element-3173">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4" data-id="element-3174">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-3175">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-3176" />
                  </svg>
                </div>
                <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-2" data-id="element-3177">
                  {isEs ? '¡Gracias por su Sugerencia!' : 'Thank You for Your Submission!'}
                </h3>
                <p className="font-nunito text-gray-600 text-sm max-w-md" data-id="element-3178">
                  {isEs ? 'Hemos recibido la información de la organización. Nuestro equipo la revisará pronto para considerarla en nuestro directorio.' : 'We have received the organization information. Our team will review it soon for consideration in our directory.'}
                </p>
              </motion.div> : <div className="space-y-5" data-id="element-3179">
                <p className="font-nunito text-gray-500 text-sm" data-id="element-3180">
                  {isEs ? 'Complete el formulario a continuación para sugerir una organización sin fines de lucro para nuestro directorio.' : 'Fill out the form below to suggest a nonprofit organization for our directory.'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3181">
                  <div data-id="element-3182">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3183">
                      {isEs ? 'Nombre de Contacto' : 'Contact Name'}{' '}
                      <span className="text-red-500" data-id="element-3184">*</span>
                    </label>
                    <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3185" />
                  </div>
                  <div data-id="element-3186">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3187">
                      {isEs ? 'Nombre de la Organización' : 'Organization Name'}{' '}
                      <span className="text-red-500" data-id="element-3188">*</span>
                    </label>
                    <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3189" />
                  </div>
                </div>

                <div data-id="element-3190">
                  <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3191">
                    {isEs ? '¿Es una organización 501(c)(3) registrada?' : 'Is this a registered 501(c)(3)?'}
                  </label>
                  <div className="flex flex-wrap gap-3" data-id="element-3192">
                    <button type="button" onClick={() => setIs501c3('yes')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'yes' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3193">
                      {isEs ? 'Sí' : 'Yes'}
                    </button>
                    <button type="button" onClick={() => setIs501c3('no')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'no' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3194">
                      {isEs ? 'No' : 'No'}
                    </button>
                    <button type="button" onClick={() => setIs501c3('not-sure')} className={`px-5 py-3 rounded-xl border font-nunito text-sm transition-all ${is501c3 === 'not-sure' ? 'bg-haa-blue text-white border-haa-blue' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-3195">
                      {isEs ? 'No estoy seguro' : 'Not Sure'}
                    </button>
                  </div>
                </div>

                <AnimatePresence data-id="element-3196">
                  {is501c3 === 'yes' && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-3197">
                      <div className="pt-1" data-id="element-3198">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3199">
                          {isEs ? 'Número EIN' : 'EIN Number'}
                        </label>
                        <input type="text" value={ein} onChange={e => setEin(e.target.value)} placeholder="XX-XXXXXXX" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3200" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3201">
                  <div data-id="element-3202">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3203">
                      {isEs ? 'Teléfono' : 'Phone'}
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3204" />
                  </div>
                  <div data-id="element-3205">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3206">
                      {isEs ? 'Correo Electrónico' : 'Email'}{' '}
                      <span className="text-red-500" data-id="element-3207">*</span>
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isEs ? 'correo@ejemplo.com' : 'email@example.com'} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3208" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3209">
                  <div data-id="element-3210">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3211">
                      {isEs ? 'Sitio Web' : 'Website'}
                    </label>
                    <input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3212" />
                  </div>
                  <div data-id="element-3213">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3214">
                      {isEs ? 'Redes Sociales' : 'Social Media Handle'}
                    </label>
                    <input type="text" value={socialMedia} onChange={e => setSocialMedia(e.target.value)} placeholder="@handle" className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3215" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3216">
                  <div data-id="element-3217">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3218">
                      {isEs ? 'Categoría' : 'Category'}
                    </label>
                    <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm bg-white" data-id="element-3219">
                      <option value="" data-id="element-3220">
                        {isEs ? '-- Seleccione --' : '-- Select --'}
                      </option>
                      {CATEGORIES.map(c => <option key={c.id} value={c.label} data-id="element-3221">
                          {isEs && c.labelEs ? c.labelEs : c.label}
                        </option>)}
                      <option value="Other" data-id="element-3222">{isEs ? 'Otro' : 'Other'}</option>
                    </select>
                  </div>
                  <div data-id="element-3223">
                    <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3224">
                      {isEs ? 'Ubicación / Área de Servicio' : 'Location / Service Area'}
                    </label>
                    <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder={isEs ? 'Ej. Houston, TX o Nacional' : 'e.g. Houston, TX or Nationwide'} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3225" />
                  </div>
                </div>

                <div data-id="element-3226">
                  <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3227">
                    {isEs ? 'Descripción de la Organización' : 'Organization Description'}{' '}
                    <span className="text-red-500" data-id="element-3228">*</span>
                  </label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder={isEs ? 'Describa brevemente la misión y los servicios de la organización...' : "Briefly describe the organization's mission and services..."} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm resize-none" data-id="element-3229" />
                </div>
              </div>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3" data-id="element-3230">
            {submitted ? <button onClick={() => {
            setModalOpen(false);
            resetForm();
          }} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3231">
                {isEs ? 'Cerrar' : 'Close'}
              </button> : <>
                <button onClick={() => setModalOpen(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3232">
                  {isEs ? 'Cancelar' : 'Cancel'}
                </button>
                <button onClick={handleSubmit} disabled={!isValid} className="flex-1 py-2.5 bg-gradient-to-r from-haa-blue to-haa-navy hover:from-haa-midblue hover:to-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3233">
                  {isEs ? 'Enviar' : 'Submit'}
                </button>
              </>}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
}